import { TenFourText } from '@/components/TenFourText'
import { Check } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useSetupSteps, type SetupStep, type SetupSteps } from './useSetupSteps'
import s from './first-run.module.css'

/**
 * The Help drawer's "Get set up" section (design system Components · 09,
 * moved from Home into Help on 2026-09-23): a group label with the count and
 * bar, then one row per step, sized for a 420–440px drawer (a container query
 * stacks the action under the text in a very narrow one). Owners only; the
 * steps come from `useSetupSteps()`, so Help and Home's setup banner always
 * agree.
 *
 * `onLeave` runs before any action that leaves the drawer (a link, the sample
 * call) so the drawer is closed first. In-place actions (Retry, and Watch,
 * which opens the video inside Help) skip it.
 */
export function HelpSetupSection({ onLeave }: { onLeave?: () => void }) {
  const setup = useSetupSteps()
  if (!setup.isOwner) return null
  return <HelpSetup setup={setup} onLeave={onLeave} />
}

function HelpSetup({ setup, onLeave }: { setup: SetupSteps; onLeave?: () => void }) {
  const { steps, done, total, next, setupRequired } = setup

  // Every step done: the section folds to one quiet line.
  if (done === total) {
    return (
      <p className={s.hsComplete} data-help-setup="complete">
        <span className={s.hsCompleteMark} aria-hidden="true">
          <Check size={12} strokeWidth={3} />
        </span>
        Setup complete
      </p>
    )
  }

  return (
    <section
      className={s.hs}
      aria-labelledby="help-setup-title"
      data-help-setup=""
      data-urgent={setupRequired ? 'true' : undefined}
    >
      <header className={s.hsHead}>
        <h3 id="help-setup-title" className={s.hsLabel}>
          Get set up
        </h3>
        <span className={s.hsCount} aria-live="polite">
          {done} of {total}
        </span>
        <span
          className={s.hsBar}
          role="progressbar"
          aria-label="Setup progress"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={done}
          aria-valuetext={`${done} of ${total} steps done`}
        >
          <i style={{ ['--p' as string]: done / total }} />
        </span>
      </header>
      <ol className={s.hsList}>
        {steps.map((step) => (
          <HelpSetupRow key={step.id} step={step} isNext={step.id === next?.id} onLeave={onLeave} />
        ))}
      </ol>
    </section>
  )
}

function HelpSetupRow({ step, isNext, onLeave }: { step: SetupStep; isNext: boolean; onLeave?: () => void }) {
  const state = step.done ? 'done' : isNext ? 'next' : 'open'
  const action = step.action
  const buttonClass = `${s.hsAction} ${isNext ? s.hsActionPrimary : s.hsActionSecondary}`

  return (
    <li className={s.hsStep} data-state={state} data-urgent={step.urgent ? 'true' : undefined}>
      <span className={s.hsMark} aria-hidden="true">
        {step.done ? <Check size={12} strokeWidth={3} /> : null}
      </span>
      <div className={s.hsText}>
        <span className={s.hsTitle}>
          <TenFourText>{step.title}</TenFourText>
          {step.done && <span className={s.srOnly}> (done)</span>}
        </span>
        <span className={s.hsSub}>{step.sub}</span>
      </div>
      {!step.done && action ? (
        action.to ? (
          <Link to={action.to} className={buttonClass} onClick={onLeave}>
            {action.label}
          </Link>
        ) : action.run ? (
          <button
            type="button"
            className={buttonClass}
            onClick={() => {
              if (!action.inPlace) onLeave?.()
              action.run?.()
            }}
          >
            {action.label}
          </button>
        ) : null
      ) : null}
    </li>
  )
}
